import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { notifications } from '@mantine/notifications'
import { authenticateUser, introspectUser } from '@/api/main/sdk.gen'
import { AccessCategory, UserIntrospectionResponse } from '@/api/main/types.gen'
import i18next from 'i18next'

interface TokenResponse {
  access_token: string
  expires_in: number
  token_type?: string
}

export interface User {
  id: string
  email: string
  name?: string
  role?: string
}

export interface AuthState {
  user: User | null
  userPermissions: UserIntrospectionResponse | null // NEW: Cached permissions
  accessToken: string | null
  expiresIn: number | null
  issuedAt: number | null
  isLoading: boolean
  refreshPromise: Promise<string> | null
}

export interface AuthActions {
  // State getters
  isAuthenticated: () => boolean
  isTokenExpired: () => boolean
  isTokenExpiringSoon: () => boolean
  getAuthHeaders: () => Record<string, string>

  // NEW: Permission helpers
  hasPermission: (resource: AccessCategory, action?: 'read' | 'write') => boolean
  hasAnyPermission: (resource: AccessCategory) => boolean

  // Actions
  setLoading: (loading: boolean) => void
  storeToken: (token: TokenResponse, user: User) => void
  clearToken: () => void
  fetchUserPermissions: () => Promise<void> // NEW: Fetch permissions
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<string>
  getValidToken: () => Promise<string>
}

export type AuthStore = AuthState & AuthActions

const AUTH_STORAGE_KEY = 'city-sense.auth'
const TOKEN_REFRESH_THRESHOLD = 300 // 5 minutes before expiry

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      userPermissions: null, // NEW: Initialize permissions
      accessToken: null,
      expiresIn: null,
      issuedAt: null,
      isLoading: false,
      refreshPromise: null,

      // State getters
      isAuthenticated: () => {
        const { accessToken, isTokenExpired, clearToken } = get()
        const expired = isTokenExpired()

        // If token is expired, clear user data to ensure consistent layout behavior
        if (expired && accessToken) {
          clearToken()
        }

        return !!accessToken && !expired
      },

      isTokenExpired: () => {
        const { issuedAt, expiresIn } = get()
        if (!issuedAt || !expiresIn || expiresIn === 0) return true

        const currentTime = Math.floor(new Date().getTime() / 1000)
        return issuedAt + expiresIn < currentTime
      },

      isTokenExpiringSoon: () => {
        const { issuedAt, expiresIn } = get()
        if (!issuedAt || !expiresIn) return true

        const currentTime = Math.floor(new Date().getTime() / 1000)
        return issuedAt + expiresIn - TOKEN_REFRESH_THRESHOLD < currentTime
      },

      getAuthHeaders: (): Record<string, string> => {
        const { accessToken } = get()
        return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
      },

      // NEW: Permission helpers
      hasPermission: (resource: AccessCategory, action?: 'read' | 'write'): boolean => {
        const { userPermissions } = get()

        if (!userPermissions?.permissions?.[resource]) return false

        if (action) {
          return userPermissions.permissions[resource][action] === true
        }

        const perms = userPermissions.permissions[resource]
        return perms.read === true || perms.write === true
      },

      hasAnyPermission: (resource: AccessCategory): boolean => {
        const { userPermissions } = get()
        if (!userPermissions?.permissions?.[resource]) return false
        const perms = userPermissions.permissions[resource]
        return perms.read === true || perms.write === true
      },

      // Actions
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      storeToken: (token: TokenResponse, user: User) => {
        set({
          accessToken: token.access_token,
          expiresIn: token.expires_in,
          issuedAt: Math.floor(new Date().getTime() / 1000),
          user,
        })
      },

      // NEW: Fetch user permissions
      fetchUserPermissions: async () => {
        const { isAuthenticated } = get()

        if (!isAuthenticated()) {
          throw new Error('User not authenticated')
        }

        try {
          const response = await introspectUser()
          if (response.data) {
            set({ userPermissions: response.data })
          }
        } catch (error) {
          console.error('Failed to fetch user permissions:', error)
          throw error
        }
      },

      clearToken: () => {
        set({
          user: null,
          userPermissions: null, // NEW: Clear permissions
          accessToken: null,
          expiresIn: null,
          issuedAt: null,
          refreshPromise: null,
        })
      },

      refreshAccessToken: async (): Promise<string> => {
        // Since refresh tokens aren't implemented in the current API,
        // we'll just throw an error to trigger re-login
        const { clearToken } = get()
        clearToken()
        throw new Error('Token refresh not implemented - please log in again')
      },

      getValidToken: async (): Promise<string> => {
        const { accessToken, isTokenExpired, isTokenExpiringSoon, refreshAccessToken } = get()

        if (!accessToken || isTokenExpired()) {
          throw new Error('No valid token available')
        }

        // Refresh token if it's expiring soon
        if (isTokenExpiringSoon()) {
          return await refreshAccessToken()
        }

        return accessToken
      },

      login: async (username: string, password: string) => {
        const { setLoading, storeToken } = get()

        setLoading(true)

        try {
          const authResponse = await authenticateUser({
            body: { username, password },
          })

          if (!authResponse.data) {
            throw new Error('No token received from server')
          }

          storeToken(authResponse.data, {
            id: 'temp',
            email: username,
            name: username.includes('@') ? username.split('@')[0] : username,
          })

          const userResponse = await introspectUser()
          if (userResponse.data) {
            set({
              user: {
                id: userResponse.data.id.toString(),
                email: userResponse.data.email,
                name: userResponse.data.email.split('@')[0],
                role: userResponse.data.role,
              },
              userPermissions: userResponse.data,
            })
          }

          notifications.show({
            title: i18next.t('loggedInTitle', { ns: 'login' }),
            message: i18next.t('loggedInMessage', { ns: 'login' }),
            color: 'green',
          })
        } catch {
          notifications.show({
            title: i18next.t('loginFailedTitle', { ns: 'login' }),
            message: i18next.t('loginFailedMessage', { ns: 'login' }),
            color: 'red',
          })
        } finally {
          setLoading(false)
        }
      },

      logout: () => {
        const { clearToken } = get()
        clearToken()
        localStorage.removeItem(AUTH_STORAGE_KEY)

        notifications.show({
          title: i18next.t('loggedOutTitle', { ns: 'login' }),
          message: i18next.t('loggedOutMessage', { ns: 'login' }),
          color: 'blue',
        })
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: state => ({
        user: state.user,
        userPermissions: state.userPermissions, // NEW: Persist permissions
        accessToken: state.accessToken,
        expiresIn: state.expiresIn,
        issuedAt: state.issuedAt,
      }),
    }
  )
)

if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__authStore = useAuthStore
}
